import { formatVND } from "@/lib/format";
import type { PublicBookingService } from "./api";

/**
 * Số tiền khách phải chuyển sau khi áp mã ưu đãi.
 *
 * Phản chiếu ĐÚNG luật của máy chủ (customerbooking/service.go:217-241) —
 * con số thật vẫn do máy chủ chốt khi tạo đơn, đây chỉ là bản xem trước để
 * trang không hiện một đằng rồi thu một nẻo. Sửa một bên phải sửa cả hai.
 *
 * Hai điểm dễ hiểu sai:
 * - Cọc là số tiền CỐ ĐỊNH của gói, không phải tỷ lệ theo giá, nên giảm giá
 *   KHÔNG làm cọc nhỏ đi — phần giảm rơi vào khoản còn lại trả tại studio.
 * - Trừ khi cọc lớn hơn cả tổng sau giảm; lúc đó thu đúng tổng sau giảm chứ
 *   không thu quá số khách nợ.
 */
export function discountedTotal(price: number, discount: number): number {
  const v = price - discount;
  return v < 0 ? 0 : v;
}

export function requiredAmount(
  price: number,
  depositAmount: number,
  discount: number,
  paymentOption: "deposit" | "full",
): number {
  const total = discountedTotal(price, discount);
  if (paymentOption === "full") return total;
  return depositAmount > total ? total : depositAmount;
}

/**
 * Chọn giữa mã khách gõ và ưu đãi Giờ Vàng — áp cái LỢI HƠN, bằng nhau thì
 * giữ mã khách gõ. Luật ở chung một chỗ với màn nội bộ (features/promos) để
 * không có hai bản luật trôi khỏi nhau; số đưa vào discountedTotal/
 * requiredAmount lấy từ đây.
 */
export { addDiscounts } from "@/features/promos/offpeak";
export type { AddedDiscounts } from "@/features/promos/offpeak";

/** Gói nhiều mục đích thì thẻ hiện giá thấp nhất kèm chữ "Từ" — giá thật do
 *  máy chủ chốt lại theo mục đích khách chọn. */
export function serviceMinPrice(service: PublicBookingService): number {
  const prices = (service.purposes ?? []).map((p) => p.price).filter((v) => v > 0);
  return prices.length > 0 ? Math.min(...prices) : service.price;
}

export function serviceMinDeposit(service: PublicBookingService): number {
  const deposits = (service.purposes ?? []).map((p) => p.depositAmount).filter((v) => v > 0);
  return deposits.length > 0 ? Math.min(...deposits) : service.depositAmount;
}

/** Nhãn giá + nhãn cọc trên thẻ gói.
 *
 *  Gói có mục đích (ảnh thẻ / CV / hãng bay) thì số tiền khách trả LUÔN là giá
 *  của mục đích — `ResolveWithPurpose` bên máy chủ ghi đè `Price` như vậy — nên
 *  thẻ phải báo giá mục đích, KHÔNG phải giá niêm yết của gói. Lấy `service.price`
 *  ở đây từng làm thẻ "CV Basic (có make)" ghi 970.000 đ đứng cạnh cọc 270.000 đ
 *  (30% của 900.000): quảng cáo đắt hơn số khách thực trả.
 *
 *  Chữ "Từ" chỉ xuất hiện khi các mục đích lệch giá nhau; đồng giá thì nói thẳng
 *  một con số cho khách khỏi phải đoán. */
export function servicePriceLabel(service: PublicBookingService): string {
  const spread = new Set((service.purposes ?? []).map((p) => p.price).filter((v) => v > 0));
  const prefix = spread.size > 1 ? "Từ " : "";
  return `${prefix}${formatVND(serviceMinPrice(service))}`;
}

export function serviceDepositLabel(service: PublicBookingService): string {
  const spread = new Set((service.purposes ?? []).map((p) => p.depositAmount).filter((v) => v > 0));
  const prefix = spread.size > 1 ? "Cọc từ " : "Cọc ";
  return `${prefix}${formatVND(serviceMinDeposit(service))}`;
}

/**
 * Giá và cọc của lựa chọn hiện tại (gói + mục đích).
 *
 * Trả `null` khi CHƯA đủ nghĩa: gói có mục đích mà khách chưa chọn mục nào.
 * Lúc đó phải hiện "Chưa chọn" chứ không mượn tạm giá niêm yết của gói —
 * mượn tạm là khách thấy 970.000 đ rồi tụt xuống 900.000 đ khi bấm "Hộ chiếu",
 * trông như giá ảo. Form đơn nội bộ cũng đưa về 0 chờ chốt lại
 * (features/schedule/components/detail/upsale.ts).
 */
export function selectedPrice(
  service: PublicBookingService | null,
  purpose: { price: number } | null,
): number | null {
  if (!service) return null;
  if ((service.purposes?.length ?? 0) > 0) return purpose ? purpose.price : null;
  return service.price;
}

export function selectedDeposit(
  service: PublicBookingService | null,
  purpose: { depositAmount: number } | null,
): number | null {
  if (!service) return null;
  if ((service.purposes?.length ?? 0) > 0) return purpose ? purpose.depositAmount : null;
  return service.depositAmount;
}

/**
 * VAT xem trước cho trang công khai.
 *
 * Phản chiếu ĐÚNG luật máy chủ (order.ComputeVATSnapshot, dùng chung với đơn
 * nội bộ): làm tròn nửa lên trên số nguyên đồng. Máy chủ vẫn chốt số thật lúc
 * tạo lịch hẹn — đây chỉ để trang không hiện một đằng rồi thu một nẻo.
 *
 * Mọi lịch hẹn công khai đều chịu VAT. Lựa chọn eShop/VAT chỉ quyết định CÁCH
 * xuất hóa đơn, KHÔNG bật/tắt thuế.
 */
export function vatAmount(taxableBase: number, rateBps: number): number {
  if (taxableBase <= 0 || rateBps <= 0) return 0;
  return Math.floor((taxableBase * rateBps + 5000) / 10000);
}

export type BookingMoney = {
  /** Giá sau khi trừ ưu đãi, chưa gồm VAT. */
  taxableBase: number;
  vatAmount: number;
  /** Tổng đã gồm VAT — số khách nợ studio. */
  payableTotal: number;
  /** Số phải chuyển ngay để giữ lịch. */
  paymentRequired: number;
  /** Phần trả nốt tại studio. */
  remaining: number;
};

/**
 * Toàn bộ số tiền của một lượt đặt, ở một chỗ.
 *
 * Cọc KHÔNG cộng VAT: cọc là số cố định của gói, nên VAT rơi trọn vào phần còn
 * lại trả tại studio (CEO chốt). Thanh toán đủ thì phải gồm VAT.
 */
export function bookingMoney(input: {
  price: number;
  depositAmount: number;
  discount: number;
  vatRateBps: number;
  paymentOption: "deposit" | "full";
}): BookingMoney {
  const taxableBase = discountedTotal(input.price, input.discount);
  const vat = vatAmount(taxableBase, input.vatRateBps);
  const payableTotal = taxableBase + vat;
  const paymentRequired =
    input.paymentOption === "full"
      ? payableTotal
      : input.depositAmount > taxableBase
        ? taxableBase
        : input.depositAmount;
  const remaining = payableTotal - paymentRequired;
  return {
    taxableBase,
    vatAmount: vat,
    payableTotal,
    paymentRequired,
    remaining: remaining < 0 ? 0 : remaining,
  };
}
